import $ from 'jquery';
import Modal from 'bootstrap/js/src/modal';
import {getDynamicValue} from '@utils/commonUtils';
import abTestService from '../services/abTestService';
import ajaxService from '../services/ajaxService';
import contentService from '../services/contentService';
import {lazyInitByScroll} from '../helpers/lazyInit';
import {
	initPaymentInstallmentsTestForArrangementList,
	initPaginatorVsLoadMoreBtnTest,
	getTestNameFromVariation
} from '../helpers/abTestHelpers';
import {lazyLoad} from '../helpers/lazyLoadHelpers';
import {initPaymentInstallmentPopovers, initPaymentInstallmentMobilePopover} from '../helpers/paymentInstallmentPopoverHelpers';
import Modals from './modals';
import StickyFiltersAndSorting from './StickyFiltersAndSorting';
import SortingComponent from './SortingComponent';
import TomSelectComponent from './TomSelectComponent';
import FavouriteArrangements from './FavouriteArrangements';

/**
 * @typedef {import('vanilla-lazyload').ILazyLoadInstance} ILazyLoadInstance
 */

const MIN_FILTERING_REQUEST_TIME_IN_MS = 300;

class ArrangementListPageContent {
	/**
	 * @param {!ArrangementListPageFrontendParams} params
	 * @param {Function} 						   formatDate
	 */
	constructor(params, formatDate) {
		this.params					= params;
		this.formatDate 			= formatDate;
		/**
		 * this contains both the filter queries and other queries
		 * @type {!Object}
		 */
		this.fullQuery 			= {};
		this.filterRequestStartDate = 0;
		this.favouriteArrangementsHandler = new FavouriteArrangements();

		/** @type {?ListPageFlatPickrElement} */
		this.flatPickrElement = null;

		/** @type {?ILazyLoadInstance} */
		this.lazyLoadApi = null;

		/** @type {IntersectionObserver[]} */
		this.observers = [];

		this.tomSelectComponent = new TomSelectComponent(this);

		this.init();
	}

	init() {
		this.resetFullQuery();
		this.storeInitialDateRange();
		this.setFiltersCount();
		this.createSortingComponent();
		this.showMobileFilterModal();
		initPaymentInstallmentPopovers();

		this.tomSelectComponent.initFiltersBlock({
			'firstInit': true
	    });
		this.initEvents();
		new Modals();

		if (this.params.withStickyFiltersAndSorting) {
			lazyInitByScroll(
				this.initStickyFiltersAndSorting.bind(this)
			);
		}
	}

	createSortingComponent() {
		new SortingComponent({
			'onSelect': (selectedSorting) => {
				this.storeSorting(selectedSorting);
				this.getFilteredArrangements();
			}
		});
	}

	initStickyFiltersAndSorting() {
		const $mobileFiltersAndSortingRow = $('.mobileFilterAndSortingRow');

		if ($mobileFiltersAndSortingRow.is(':hidden')) {
			return;
		}

		const stickyFiltersAndSortingComponent = new StickyFiltersAndSorting(
			$mobileFiltersAndSortingRow,
			$('.filteredArrangementsContainer')
		);

		// update stickiness when the infoLine is closed
		$('#closeInfoLine, #closeInfoLine i').one(
			'click',
			() => {
				stickyFiltersAndSortingComponent.setStickyMinScrollTop();
				stickyFiltersAndSortingComponent.checkForUpdate({
					'forceRefresh': true
				});
			}
		);
	}

	showMobileFilterModal() {
		if (window.innerWidth <= 767) {
			const filterGroupsModal = document.getElementById('filterGroupsModal');

			if (filterGroupsModal) {
				Modal
					.getOrCreateInstance(filterGroupsModal)
					.show();
			}
		}
	}
	
	/**
	 * @param {!jQuery} $dateSelector
	 * @param {?Object} [filteredDateRange]
	 */
	createFlatPickrElement($dateSelector, filteredDateRange) {
		import(
			/* webpackChunkName: "ListPageFlatPickrElement" */
			'./ListPageFlatPickrElement'
		)
		.then(({'default': ListPageFlatPickrElement}) => {
			this.flatPickrElement = new ListPageFlatPickrElement({
				'listPageObject'       : this,
				'arrangementsDateRange': this.params.arrangementsDateRange,
				'dictionary'           : this.params.dictionary,
				$dateSelector,
				filteredDateRange
			});
		});
	}

	/**
	 * Calculates the bottom position in pixels of the specified element from the top of the document.
	 * @param {jQuery} $element - The jQuery object of the element.
	 * @returns {number} - The bottom position in pixels.
	 */
	getBottomPositionFromTop($element) {
		return $element.offset().top + $element.outerHeight();
	}

	/**
	 * Checks if the load more button element exists.
	 * @returns {boolean} True if the load more button element exists, false otherwise.
	 */
	hasLoadMoreButton() {
		return !!document.getElementById('loadMoreButton');
	}

	/**
	 * Retrieves the paginator elements or the load more button based on the flag.
	 * @param {boolean} isPaginatorElements Flag to determine whether to return paginator elements or load more button.
	 * @returns {NodeList|HTMLElement|null} Paginator elements or load more button.
	 */
	getPaginatorElements(isPaginatorElements = true) {
		if (isPaginatorElements) {
			return document.querySelectorAll('.paginationBlockContainer .page-item');
		}
 
			return document.getElementById('loadMoreButton');
	}

	/**
	 * Event handler for paginator elements click.
	 * @param {Event} event - The click event.
	 * @returns {void}
	 */
	paginatorElementClickHandler(event) {
		if (event.ctrlKey || event.metaKey || event.shiftKey) {
			return; // let the browser deal with the click natively
		}

		event.preventDefault();

		const pageItem = $(event.currentTarget);

		if (pageItem.hasClass('active')) {
			return;
		}

		this.storePageNumber(
			pageItem.find('.page-link').data('pageIndex')
		);

		this.getFilteredArrangements();
		this.scrollToTop({
			'$element': $('.listPageTitle')
		});
	}

	/**
	 * Event handler for the click event on the load more element.
	 * @param {Event} event - The click event.
	 * @returns {void}
	 */
	loadMoreElementClickHandler(event) {
		event.preventDefault();

		this.loadMoreNext();

		this.getFilteredArrangements();

		this.scrollToTop({
			'$element'     : $('.paginationBlockContainer'),
			'toTopPosition': this.getBottomPositionFromTop($('.arrangementsListContainer')),
			'correction'   : -80
		});
	}

	/**
	 * Adds click event listeners to paginator elements.
	 * @returns {void}
	 */
	initPaginatorElementsEvents() {
		const paginatorElements = this.getPaginatorElements();

		if (paginatorElements) {
			paginatorElements.forEach((paginatorElement) => {
				paginatorElement.addEventListener('click', this.paginatorElementClickHandler.bind(this));
			});
		}
	}

	/**
	 * Adds a click event listener to the load more element.
	 * @returns {void}
	 */
	initLoadMoreButtonEvents() {
		const loadMoreElement = this.getPaginatorElements(false);

		if (loadMoreElement) {
			loadMoreElement.addEventListener('click', this.loadMoreElementClickHandler.bind(this));
		}
	}

	initEvents() {
		let lastClickStamp = 0;

		$(document.body).on('click', '.removeAllFilters', this.removeAllFilters.bind(this));

		$('.activeFilterTagsContainer').on('click', '.removeFilter', (ev) => {
			const clicked		= $(ev.currentTarget),
				  clickStamp 	= Date.now(),
				  diff 			= (clickStamp - lastClickStamp);

			if (diff > 300) {
				this.removeActiveFilterById(clicked.data('filterId'));
			}

			lastClickStamp = clickStamp;
		});

		$('.filterGroupContainer').on('change', '.filterCheckbox', (ev) => {
			const clicked = $(ev.currentTarget);

			this.storeFiltersByElement(clicked);
			this.getFilteredArrangements();
		});

		if (this.hasLoadMoreButton()) {
			this.initLoadMoreButtonEvents();
		} else {
			this.initPaginatorElementsEvents();
		}

		$('.arrangementListItem .bookBtn').on('click', () => {
			dataLayer.push({
			    'event'        : 'View Packages CTA',
			    'eventCategory': 'Ecommerce',
			    'eventAction'  : 'Event_View_Pack_CTA',
			    'eventLabel'   : 'Booking-Engine'
			});
		});
		
		initPaymentInstallmentsTestForArrangementList();

		// TODO: ET283_ab_test_pagination_vs_load_more - Remove this when the test is over (FBT,VBT,DKT,FBR,DET)
		initPaginatorVsLoadMoreBtnTest();
		
		const offersFilteringTrackingEventName = globalParams.pageType === 'arrangementList' ? 'Offers_checked_find_your_trip' : 'Offers_checked_clubs';
		
		$('.isTopRecommendedFilterGroupContainer input').on('change', () => {
			dataLayer.push(offersFilteringTrackingEventName);
		});

		const productBenefitsABTestVariations = ['showProductBenefitsInArrangementList_no', 'showProductBenefitsInArrangementList_yes'];
		
		if (abTestService.isAnyVariationActive(productBenefitsABTestVariations)) {
			$('.filteredArrangementsContainer').on(
				'mousedown',
				'.arrangementListItem-matchName, .arrangementListItem-logos-wrapper, .bookBtn',
				() => {
					abTestService.sendEvent(
						getTestNameFromVariation(...productBenefitsABTestVariations),
						'arrangementLinkClicked'
					);
				}
			);
		}
	}

	getFilteredArrangements() {
		const fullQuery = $.extend({}, this.fullQuery);

		this.beforeFilteringRequest(fullQuery);
		this.fetchFilteredArrangements(fullQuery)
			.then(this.handleFilteringSuccess.bind(this))
			.catch(this.handleFilteringError.bind(this));
	}

	beforeFilteringRequest(fullQuery) {
		if (this.params.beforeFilteringRequest) {
			this.params.beforeFilteringRequest(fullQuery);
		}

		this.addLoader();
		this.disableTomSelects();
		this.filterRequestStartDate = Date.now();
	}

	fetchFilteredArrangements(fullQuery) {
		const fullQueryString = this.getFullQueryString(fullQuery);

		return ajaxService.sendGet(`arrangement/filtered_scheduled/${fullQueryString}`);
	}

	handleFilteringSuccess(response) {
		const requestTime = Date.now() - this.filterRequestStartDate;

		if (requestTime >= MIN_FILTERING_REQUEST_TIME_IN_MS) {
			this.updatePageByResponse(response);
		} else {
			setTimeout(
				this.updatePageByResponse.bind(this, response),
				MIN_FILTERING_REQUEST_TIME_IN_MS - requestTime
			);
		}

		// remove the page number, don't handle it with further filtering
		this.storePageNumber();
		this.setFiltersCount();
	}

	handleFilteringError() {
		this.removeLoader();
	}

	updatePageByResponse(response) {
		if (response) {
			this.updateFilteredArrangementsCountHolders(response.filteredArrangementsCount);

			if (typeof response.filteredArrangementsBlock === 'string') {
				$('.filteredArrangementsContainer').html(response.filteredArrangementsBlock);
				
				this.favouriteArrangementsHandler.setFavoritedIconsLook();
			}

			if (response.filterGroupsBlock) {
				$('.filterGroupContainer .modal-body').html(response.filterGroupsBlock);
				this.tomSelectComponent.initFiltersBlock({response});
				
				this.enableTomSelects();
			}

			this.lazyLoadElements('.arrangementListItem .clubLogo, .option .clubLogo');

			for (const observer of this.observers) {
				observer.disconnect();
			}

			this.observers = contentService.initPopovers({
				'params': contentService.customPopOverMarkup()
			});

			requestAnimationFrame(() => {
				if (typeof response.activeFilterTagsBlock === 'string' && this.params.showActiveFilterTags) {
					$('.activeFilterTagsContainer').html(response.activeFilterTagsBlock);
				}

				$('.paginationBlockContainer').html(response.paginationBlock || '');

				this.renderLoadMoreTexts(response);
				this.initPaginatorElementsEvents();
				initPaymentInstallmentMobilePopover();
				this.updateHistory(response.newQueryString);
			});
		}
	}

	updateFilteredArrangementsCountHolders(count) {
		if (typeof count !== 'undefined') {
			$('.listPageTitle').text(
				this.getTranslation(count > 1 ? 'sub_header_plural' : 'sub_header',
					{count})
			);
			$('.closeModal').text(
				this.getTranslation(count > 1 ? 'show_results_plural' : 'show_results',
					{count})
			);
		}
	}

	lazyLoadElements(selector) {
		this.lazyLoadApi?.destroy();

		if (document.querySelector(selector)) {
			this.lazyLoadApi = lazyLoad({
				'elements_selector': selector
			});
		} else {
			this.lazyLoadApi = null;
		}
	}

	updateHistory(newQueryString) {
		if (this.params.allowHistoryUpdate) {
			history.replaceState(null, null, `${location.pathname}${newQueryString || ''}`);
		}
	}

	resetFullQuery() {
		this.fullQuery = {
			...this.params.mandatoryFullQuery
		};
		this.storeSorting(this.getCurrentSorting());
		this.storeAllSelectedFilters();
	}

	getFullQueryString(fullQuery) {
		const validFullQuery = this.getValidFullQuery(fullQuery);

		let fullQueryString = '';

		if (Object.keys(validFullQuery).length) {
			const searchParams = new URLSearchParams(validFullQuery).toString();

			fullQueryString = `?${searchParams}`;
		}

		return fullQueryString;
	}

	getValidFullQuery(fullQuery) {
		const activeFilters = {};

		$.each(fullQuery, (filterName, filterValue) => {
			if (!this.seemsValidFilter(filterName, filterValue)) {
				return;
			}

			if (Array.isArray(filterValue)) {
				if (filterValue.length) {
					activeFilters[filterName] = filterValue.join(',');
				}
			} else if (filterValue instanceof Date) {
				activeFilters[filterName] = this.formatDate(filterValue, this.params.DATE_FILTER_FORMAT);
			} else {
				activeFilters[filterName] = filterValue;
			}
		});

		return activeFilters;
	}

	getActiveFiltersCount() {
		const {QUERY_KEYS, FILTER_QUERY_NAMES} 	= this.params,
			  nonFilteringKeys 					= [QUERY_KEYS.SORT, QUERY_KEYS.PAGE_NUMBER],
			  validFullQuery 					= this.getValidFullQuery(this.fullQuery);

		let filtersCount = 0;

		$.each(validFullQuery, (filterName, filterValue) => {
			/*
			 * date_range should only count as 1 filter so we skip the end range
			 * don't count non filtering values
			 */
			if (filterName === FILTER_QUERY_NAMES.DATE_RANGE_END || nonFilteringKeys.includes(filterName)) {
				return;
			}

			filtersCount += filterValue.toString().split(',').length;
		});

		return filtersCount;
	}

	/**
	 * @param {string} filterName
	 * @param {string} filterValue
	 * @returns {boolean}
	 */
	seemsValidFilter(filterName, filterValue) {
		const FILTER_GROUPS_WITH_BOOLEAN_FILTERS = this.params.FILTER_GROUPS_WITH_BOOLEAN_FILTERS || [];

		return !!filterValue || FILTER_GROUPS_WITH_BOOLEAN_FILTERS.includes(filterName);
	}

	storeInitialDateRange() {
		const {filteredDateRange} = this.params;

		if (filteredDateRange) {
			this.storeDateRange(
				Object.values(filteredDateRange)
			);
		}
	}

	storeDateRange(dates) {
		const isValidRange = !dates.length || dates.length === 2;

		if (isValidRange) {
			const {
					  'FILTER_QUERY_NAMES': {
						  DATE_RANGE_START,
						  DATE_RANGE_END
					  }
				  } = this.params;

			this.fullQuery[DATE_RANGE_START] = dates[0];
			this.fullQuery[DATE_RANGE_END]   = dates[1];
		}
	}

	storePageNumber(pageIndex) {
		// don't send invalid or 0 value, just > 0 values
		this.fullQuery[this.params.QUERY_KEYS.PAGE_NUMBER] = pageIndex ? pageIndex + 1 : null;
	}

	loadMoreNext() {
		const limit = parseInt(this.fullQuery[this.params.QUERY_KEYS.LIMIT]);

		this.fullQuery[this.params.QUERY_KEYS.LIMIT] = limit + this.params.MAX_ARRANGEMENTS_PER_PAGE;
	}

	storeSorting(sorting) {
		this.fullQuery[this.params.QUERY_KEYS.SORT] = sorting === this.params.DEFAULT_SORTING ? null : sorting;
	}

	storeAllSelectedFilters() {
		let firstCheckedFilter;

		$('.filter-block').each((_, filterBlock) => {
			firstCheckedFilter = $(filterBlock).find('.filterCheckbox:checked').first();

			if (firstCheckedFilter.length) {
				this.storeFiltersByElement(firstCheckedFilter);
			}
		});

		this.storeDerbyFilter();
		this.storeAllEventFilters($('.tomselect__event').val());
		this.storeAllSecondaryEventFilters($('.tomselect__secondary_event').val());
	}

	storeDerbyFilter() {
		if ($('.derby-filter-input').is(':checked')) {
			const filterName = this.params.FILTER_QUERY_NAMES.DERBY;

			this.fullQuery[filterName] = '';
		}
	}

	storeAllEventFilters(defaultValue = null) {
		const {
				mandatoryFullQuery, FILTER_QUERY_NAMES
			  } 	   = this.params,
			  eventIds = (mandatoryFullQuery && mandatoryFullQuery[FILTER_QUERY_NAMES.EVENT]) || [],
			  tomSelectInstance = this.tomSelectComponent.getTomselectInstanceById('event');

		if (tomSelectInstance) {
			tomSelectInstance.items.forEach((eventId) => {
				if (!eventIds.includes(eventId)) {
					eventIds.push(eventId);
				}
			});
		} else if (Array.isArray(defaultValue) && defaultValue.length > 0) {
			eventIds.push(defaultValue);
		}

		const filterName = FILTER_QUERY_NAMES.EVENT;

		this.fullQuery[filterName] = eventIds.length ? eventIds : null;
	}

	storeAllSecondaryEventFilters(defaultValue = null) {
		if (!this.tomSelectComponent.tomSelectInstances) {
			return;
		}

		const {
				mandatoryFullQuery, FILTER_QUERY_NAMES
			  } 	   = this.params,
			  eventIds = (mandatoryFullQuery && mandatoryFullQuery[FILTER_QUERY_NAMES.OPPONENT_EVENT]) || [],

		 tomSelectInstance = this.tomSelectComponent.getTomselectInstanceById('secondary_event');

		if (tomSelectInstance) {
			tomSelectInstance.getValue().forEach((eventId) => {
				if (!eventIds.includes(eventId)) {
					eventIds.push(eventId);
				}
			});
		} else if (Array.isArray(defaultValue) && defaultValue.length > 0) {
			eventIds.push(defaultValue);
		}

		this.fullQuery[FILTER_QUERY_NAMES.OPPONENT_EVENT] = eventIds.length ? eventIds : null;
	}
	
	toggleEventTypeFilterCheckbox(filterId) {
		const filterValue      = filterId.split('__').pop(),
		      $eventTypeFilter = $(`.event-type-mobile-filter-group .event-type-filter[value=${filterValue}]`);
		
		$eventTypeFilter.prop('checked', !$eventTypeFilter.prop('checked'));
	}

	storeFiltersByElement(filterElement) {
		const filterBlock 		= filterElement.closest('.filter-block'),
			  filterGroupName 	= filterBlock.data('filterGroupName'),
			  selectedFilters 	= [];

		filterBlock
			.find('.filterCheckbox:checked')
			.each((_, checkbox) => selectedFilters.push(checkbox.value));

		this.fullQuery[filterGroupName] = selectedFilters;
	}

	/**
	 * @param {string} rawFilterId
	 */
	removeActiveFilterById(rawFilterId) {
		const {FILTER_QUERY_NAMES} 	= this.params,
		      filterId              = rawFilterId.toString(),
			  isDateRangeFilter     = filterId.startsWith(FILTER_QUERY_NAMES.DATE_RANGE),
			  isDerbyFilter         = filterId.startsWith(FILTER_QUERY_NAMES.DERBY),
			  isEventTypeFilter		= filterId.startsWith(FILTER_QUERY_NAMES.EVENT_TYPE),
			  isEventFilter		    = !isEventTypeFilter && filterId.startsWith(FILTER_QUERY_NAMES.EVENT),
			  isOpponentEventFilter = filterId.startsWith(FILTER_QUERY_NAMES.OPPONENT_EVENT);

		let shouldGetFilteredArrangements = true;

		if (isDateRangeFilter) {
			this.removeDateRangeFilter();
		} else if (isDerbyFilter) {
			this.removeDerbyFilter();
		} else if (isEventFilter) {
			const eventId = filterId.split('__').pop();
			
			this.removeEventFilter(eventId);
			this.storeAllEventFilters();
		} else if (isOpponentEventFilter) {
			const eventId = filterId.split('__').pop();
			
			this.removeSecondaryEventFilter(eventId);
			this.storeAllSecondaryEventFilters();
		} else {
			const $filterInput = $(`#${filterId}:input`);

			if ($filterInput.length) {
				$filterInput.trigger('click');
			}

			shouldGetFilteredArrangements = false;
		}

		if (isEventTypeFilter) {
			this.toggleEventTypeFilterCheckbox(filterId);
		}
		
		if (shouldGetFilteredArrangements) {
			this.getFilteredArrangements();
		}
	}
	
	removeDerbyFilter() {
		$('.derby-filter-input').prop('checked', false);
		delete this.fullQuery[this.params.FILTER_QUERY_NAMES.DERBY];
	}
	
	removeEventTypeFilters() {
		$('.event-type-mobile-filter-group .event-type-filter').prop('checked', false);
	}
	
	/**
	 * @param {string|number} eventId
	 */
	removeEventFilter(eventId) {
		if (!this.tomSelectComponent.tomSelectInstances) {
			return;
		}

		const tomSelectInstance = this.tomSelectComponent.getTomselectInstanceById('event');

		if (tomSelectInstance) {
			tomSelectInstance.removeItem(eventId);
		}
	}

	/**
	 * @param {string|number} eventId
	 */
	removeSecondaryEventFilter(eventId) {
		if (!this.tomSelectComponent.tomSelectInstances) {
			return;
		}

		const tomSelectInstance = this.tomSelectComponent.getTomselectInstanceById('secondary_event');

		if (tomSelectInstance) {
			tomSelectInstance.removeItem(eventId);
		}
	}

	/**
	 * @returns {boolean}
	 */
	hasDateRangeFilter() {
		const {
				  'FILTER_QUERY_NAMES': {
					  DATE_RANGE_START,
					  DATE_RANGE_END
				  }
			  } = this.params;

		return !!this.fullQuery[DATE_RANGE_START] && !!this.fullQuery[DATE_RANGE_END];
	}

	getSelectedSortingElement() {
		return $('.customSelect .select-item.selected');
	}

	getCurrentSorting() {
		return this.getSelectedSortingElement().data('value');
	}

	getCurrentPageNumber() {
		return $('.paginationBlockContainer .page-item.active .page-link').data('pageIndex');
	}

	unselectElements($element, selectedType) {
		$element.prop(selectedType, false);
		this.tomSelectComponent.tomSelectInstances.forEach((tomSelectInstance) => {
			tomSelectInstance.clear();
		});
	}

	removeAllFilters() {
		if (!this.getActiveFiltersCount()) {
			return;
		}

		this.unselectElements($('.item input'), 'checked');
		this.removeDateRangeFilter();
		this.removeDerbyFilter();
		this.removeEventTypeFilters();
		this.resetFullQuery();

		// store the page number because we don't want to clear it in this case
		this.storePageNumber(this.getCurrentPageNumber());
		this.getFilteredArrangements();
	}

	removeDateRangeFilter() {
		if (this.flatPickrElement) {
			this.flatPickrElement.clearSelectedDateRange();
		}
	}

	setFiltersCount(count = this.getActiveFiltersCount()) {
		if (count > 0) {
			$('.count-container').removeClass('not-show');
			$('.count-container .count').text(count);
		} else {
			$('.count-container').addClass('not-show');
		}
	}

	disableTomSelects() {
		if (!this.tomSelectComponent.tomSelectInstances) {
			return;
		}

		this.tomSelectComponent.tomSelectInstances.forEach((tomSelectInstance) => {
			tomSelectInstance && tomSelectInstance.disable();
		});
	}

	enableTomSelects() {
		if (!this.tomSelectComponent.tomSelectInstances) {
			return;
		}

		this.tomSelectComponent.tomSelectInstances.forEach((tomSelectInstance) => {
			tomSelectInstance && tomSelectInstance.enable();
		});
	}

	getTranslation(key, params) {
		const result = this.params.dictionary[key];

		if (params) {
			return getDynamicValue(result, params);
		}

		return result;
	}

	/**
	 * Scrolls to the top position of the specified element or a custom position.
	 * @param {Object} options - The options for scrolling.
	 * @param {jQuery} options.$element - The jQuery object of the element to scroll to its top position. If not provided, a custom position will be used.
	 * @param {number} options.toTopPosition - The custom position to scroll to. This is used when `$element` is not provided.
	 * @param {number} options.correction - The correction value to adjust the final scroll position. Default is -20.
	 */
	scrollToTop(options) {
		const defaults = {
				'correction': -20
			  },
			  settings = $.extend({}, defaults, options),
			  toPosition = settings.toTopPosition || settings.$element.offset().top;
		
		$('html, body').animate({
			'scrollTop': toPosition + settings.correction
		});
	}

	renderLoadMoreTexts(response) {
		const loadMoreTotal = response.filteredArrangementsCount,
			  loadMoreCount = $('.matchItem').length;

		$('.loadMoreTotal')
			.html(loadMoreTotal)
			.attr('data-max', loadMoreTotal);

		$('.loadMoreCount').text(loadMoreCount);

		$(".loadMoreContent[data-has-more-items='true']").toggleClass('d-none', loadMoreTotal === loadMoreCount);
		$(".loadMoreContent[data-has-more-items='false']").toggleClass('d-none', loadMoreTotal !== loadMoreCount);
	}

	addLoader() {
		$('.filteredArrangementsContainer').append('<div class="loader"></div>');
	}

	removeLoader() {
		$('.loader').remove();
	}

}

export default ArrangementListPageContent;